import { useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import AdminLayout from "./AdminLayout";

const input = "w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-brandorange";
const label = "text-xs font-bold uppercase tracking-wide text-gray-400";

export default function AdminSettings() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setData(r.data)).catch(() => setData({}));
  }, []);
  const set = (k) => (e) => setData({ ...data, [k]: e.target.value });
  const setSocial = (k) => (e) => setData({ ...data, social: { ...(data.social || {}), [k]: e.target.value } });

  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/settings", { data }); toast.success("Settings saved — refresh to see site-wide changes"); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  if (!data) return <AdminLayout title="Settings"><div className="h-96 skeleton rounded-2xl" /></AdminLayout>;

  const field = (key, lbl) => <div><span className={label}>{lbl}</span><input data-testid={`setting-${key}`} className={`${input} mt-1`} value={data[key] || ""} onChange={set(key)} /></div>;

  return (
    <AdminLayout title="Site Settings (WordPress-style)">
      <div className="max-w-3xl space-y-6">
        <div className="rounded-2xl border border-gray-100 bg-white p-6 card-soft">
          <h3 className="mb-4 font-display text-lg font-bold text-navy">Brand & Contact</h3>
          <div className="grid gap-4 sm:grid-cols-2">
            {field("brand_name", "Brand Name")}{field("logo", "Logo URL")}
            {field("phone", "Phone")}{field("whatsapp", "WhatsApp Number (91...)")}
            {field("email", "Email")}{field("address", "Address")}
            {field("gst_number", "GST Number")}
          </div>
        </div>
        <div className="rounded-2xl border border-gray-100 bg-white p-6 card-soft">
          <h3 className="mb-4 font-display text-lg font-bold text-navy">Homepage Hero</h3>
          <div className="space-y-4">
            {field("hero_title", "Hero Title")}
            <div><span className={label}>Hero Subtitle</span><textarea rows={2} className={`${input} mt-1`} value={data.hero_subtitle || ""} onChange={set("hero_subtitle")} /></div>
            {field("hero_image", "Hero Image URL")}
            <div><span className={label}>Footer Text</span><textarea rows={2} className={`${input} mt-1`} value={data.footer_text || ""} onChange={set("footer_text")} /></div>
          </div>
        </div>
        <div className="rounded-2xl border border-gray-100 bg-white p-6 card-soft">
          <h3 className="mb-4 font-display text-lg font-bold text-navy">Social Links</h3>
          <div className="grid gap-4 sm:grid-cols-3">
            <div><span className={label}>Instagram</span><input className={`${input} mt-1`} value={data.social?.instagram || ""} onChange={setSocial("instagram")} /></div>
            <div><span className={label}>Facebook</span><input className={`${input} mt-1`} value={data.social?.facebook || ""} onChange={setSocial("facebook")} /></div>
            <div><span className={label}>YouTube</span><input className={`${input} mt-1`} value={data.social?.youtube || ""} onChange={setSocial("youtube")} /></div>
          </div>
        </div>
        <button onClick={save} disabled={saving} data-testid="save-settings-btn" className="btn-gradient flex items-center gap-2 rounded-full px-8 py-3.5 font-bold disabled:opacity-70">{saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />} Save Settings</button>
      </div>
    </AdminLayout>
  );
}
